import React, { useState } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const PriceTrendsChart = ({ data, timeRange }) => {
  const [chartType, setChartType] = useState('area');
  const [showCost, setShowCost] = useState(true);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-premium-lg">
          <p className="text-sm font-medium text-popover-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: entry?.color }}
                />
                <span className="text-sm text-popover-foreground">{entry?.name}:</span>
              </div>
              <span className="text-sm font-medium text-popover-foreground">
                ${entry?.value?.toLocaleString()}
              </span>
            </div>
          ))}
          {payload?.[0]?.payload?.roi !== undefined && (
            <div className="mt-2 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">ROI:</span>
                <span className={`text-sm font-medium ${
                  payload?.[0]?.payload?.roi >= 0 ? 'text-success' : 'text-destructive'
                }`}>
                  {payload?.[0]?.payload?.roi >= 0 ? '+' : ''}{payload?.[0]?.payload?.roi}%
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const formatXAxis = (value) => {
    const date = new Date(value);
    return date?.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const formatYAxis = (value) => {
    if (value >= 1000000) return `$${(value / 1000000)?.toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000)?.toFixed(0)}k`;
    return `$${value}`;
  };

  const renderChart = () => {
    if (chartType === 'area') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#A0A3B1" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="#A0A3B1" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis 
              dataKey="date" 
              stroke="#A0A3B1" 
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={formatXAxis}
            />
            <YAxis 
              stroke="#A0A3B1" 
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={formatYAxis}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="circle"
            />
            <Area 
              type="monotone" 
              dataKey="value" 
              name="Portfolio Value"
              stroke="#D4AF37" 
              strokeWidth={3}
              fill="url(#colorValue)"
            />
            {showCost && (
              <Area 
                type="monotone" 
                dataKey="cost" 
                name="Cost Basis"
                stroke="#A0A3B1" 
                strokeWidth={2}
                strokeDasharray="5 5"
                fill="url(#colorCost)"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
          <XAxis 
            dataKey="date" 
            stroke="#A0A3B1" 
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={formatXAxis}
          />
          <YAxis 
            stroke="#A0A3B1" 
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={formatYAxis}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="circle"
          />
          <Line 
            type="monotone" 
            dataKey="value" 
            name="Portfolio Value"
            stroke="#D4AF37" 
            strokeWidth={3}
            dot={{ fill: '#D4AF37', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#D4AF37', strokeWidth: 2 }}
          />
          {showCost && (
            <Line 
              type="monotone" 
              dataKey="cost" 
              name="Cost Basis"
              stroke="#A0A3B1" 
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground mb-1">Price Trends</h3>
          <p className="text-sm text-muted-foreground">Portfolio value over time ({timeRange})</p>
        </div>
        
        <div className="flex items-center space-x-4 mt-4 sm:mt-0">
          {/* Chart Type Toggle */}
          <div className="flex bg-muted/30 rounded-lg p-1">
            <button
              onClick={() => setChartType('area')}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                chartType === 'area' ?'bg-primary text-primary-foreground shadow-sm' :'text-muted-foreground hover:text-card-foreground'
              }`}
            >
              <Icon name="AreaChart" size={16} />
              <span className="hidden sm:inline">Area</span>
            </button>
            <button
              onClick={() => setChartType('line')}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                chartType === 'line' ?'bg-primary text-primary-foreground shadow-sm' :'text-muted-foreground hover:text-card-foreground'
              }`}
            >
              <Icon name="TrendingUp" size={16} />
              <span className="hidden sm:inline">Line</span>
            </button>
          </div>

          {/* Show Cost Toggle */}
          <button
            onClick={() => setShowCost(!showCost)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              showCost
                ? 'bg-primary/10 text-primary border border-primary/20' :'bg-muted/50 text-muted-foreground hover:bg-muted'
            }`}
          >
            <Icon name="Eye" size={16} />
            <span className="hidden sm:inline">Cost Basis</span>
          </button>
        </div>
      </div>

      {data?.length > 0 ? (
        renderChart()
      ) : (
        <div className="flex flex-col items-center justify-center h-96 text-center">
          <Icon name="TrendingUp" size={48} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-2">No trend data available</p>
          <p className="text-sm text-muted-foreground">Add items to your collections to see price trends</p>
        </div>
      )}
    </div>
  );
};

export default PriceTrendsChart;